/**
 * WEBSOCKET / SIGNALR FAN SERVICE: SECUENCIA (SIMON SAYS MULTIPLAYER)
 * Wrapper de comunicación en tiempo real para el Aficionado Móvil / Participante de Secuencia.
 * Gestiona la conexión persistente con SignalR Hub (`/hub/entertainment`) para recibir la configuración de pads,
 * cambios de fase (WAITING, PLAYBACK, PLAYER_INPUT, WON, LOST), telemetría de turnos, y eventos de desalojo 'kicked_out'.
 * 
 * @module utilities/WS/SequenceFanWS
 */

import SignalRService from './SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes generales.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el dispositivo móvil del aficionado al Hub de SignalR con el rol 'sequence:player'.
 * Se une a la sala especificada (`room:{roomId}` o `screenId`) para recibir la telemetría en tiempo real.
 * 
 * Eventos procesados:
 * - `sequence.layout`: Configuración de botones y aviso de inicio de secuencia.
 * - `sequence.update`: Actualización de estado en vivo (pasos completados, cambio de fase, victoria o derrota).
 * - `sequence.reset`: Reinicio de la sala al estado 'WAITING'.
 * - `sequence.frontpage`: Notificación de portada en pantalla gigante.
 * - `kicked_out`: Notificación de desalojo forzoso si la sala o rol es reasignado.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback general para procesar mensajes y eventos emitidos por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado de conexión ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string|number} [roomId=''] - Identificador GUID o numérico de la sala / pantalla.
 * @param {Object} [eventCallbacks={}] - Callbacks específicos por tipo de evento.
 * @param {Function} [eventCallbacks.onLayout=null] - Callback específico para `sequence.layout`.
 * @param {Function} [eventCallbacks.onUpdate=null] - Callback específico para `sequence.update`.
 * @param {Function} [eventCallbacks.onReset=null] - Callback específico para `sequence.reset`.
 * @param {Function} [eventCallbacks.onFrontPage=null] - Callback específico para `sequence.frontpage`.
 * @param {Function} [eventCallbacks.onKickedOut=null] - Callback específico para `kicked_out`.
 */
export const ConnectWebSocketForSequenceFan = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = '',
    eventCallbacks = {}
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala / screenId
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        // 1. FILTRADO POR SALA / SCREEN ID
        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.screenId
            || messageEnvelope.ScreenId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.payload?.screenId
            || messageEnvelope.payload?.ScreenId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.screenId;

        if (roomId && envelopeRoomId !== undefined && envelopeRoomId !== null) {
            if (String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
                return;
            }
        }

        const rawType = String(messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();
        const payloadData = messageEnvelope.payload !== undefined ? messageEnvelope.payload : messageEnvelope.data;

        // 2. MANEJO PRIORITARIO DEL EVENTO KICKED_OUT
        if (rawType === 'kicked_out') {
            console.warn('[SequenceFanWS] Evento kicked_out recibido para esta sala:', messageEnvelope);

            if (typeof eventCallbacks.onKickedOut === 'function') {
                try {
                    eventCallbacks.onKickedOut(payloadData, messageEnvelope);
                } catch (kickErr) {
                    console.error('[SequenceFanWS] Error en onKickedOut callback:', kickErr);
                }
            }
        }

        // 3. DESPACHO A CALLBACKS ESPECÍFICOS SEGÚN TIPO DE EVENTO
        if (rawType === 'sequence.layout' && typeof eventCallbacks.onLayout === 'function') {
            try {
                eventCallbacks.onLayout(payloadData, messageEnvelope);
            } catch (err) {
                console.error('[SequenceFanWS] Error en callback onLayout:', err);
            }
        } else if (rawType === 'sequence.update' && typeof eventCallbacks.onUpdate === 'function') {
            try {
                eventCallbacks.onUpdate(payloadData, messageEnvelope);
            } catch (err) {
                console.error('[SequenceFanWS] Error en callback onUpdate:', err);
            }
        } else if (rawType === 'sequence.reset' && typeof eventCallbacks.onReset === 'function') {
            try {
                eventCallbacks.onReset(payloadData, messageEnvelope);
            } catch (err) {
                console.error('[SequenceFanWS] Error en callback onReset:', err);
            }
        } else if (rawType === 'sequence.frontpage' && typeof eventCallbacks.onFrontPage === 'function') {
            try {
                eventCallbacks.onFrontPage(payloadData, messageEnvelope);
            } catch (err) {
                console.error('[SequenceFanWS] Error en callback onFrontPage:', err);
            }
        }

        // 4. DESPACHAR AL HANDLER GENERAL
        if (typeof onIncomingMessage === 'function') {
            try {
                onIncomingMessage(messageEnvelope);
            } catch (err) {
                console.error('[SequenceFanWS] Error en onIncomingMessage general:', err);
            }
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'sequence:player',
        roomId: roomId ? String(roomId) : '',
        screenId: roomId ? String(roomId) : ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[SequenceFanWS] Error al conectar aficionado con SignalR Hub:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del aficionado móvil y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForSequenceFan = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[SequenceFanWS] Error al desconectar la sesión de SignalR del aficionado:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForSequenceFan,
    DisconnectWebSocketForSequenceFan
};
